import { Clock, Heart, Soup, Star } from "lucide-react";
import type { Recipe } from "@/types";
import type { Lang } from "@/i18n";
import { T } from "@/i18n";
import { Card } from "@/components/ui/card";
import { langPath, navigate } from "@/router";
import { DIFFICULTIES } from "@/filters";
import { useFavorites, useMounted } from "@/favorites";

function difficultyLabel(d: Recipe["difficulty"], lang: Lang): string {
  return DIFFICULTIES.find(x => x.value === d)?.label[lang] ?? d;
}

export function RecipeCard({
  recipe,
  lang,
  titleHtml,
}: {
  recipe: Recipe;
  lang: Lang;
  titleHtml?: string;
}) {
  const href = langPath(lang, recipe.slug);
  const { has, toggle } = useFavorites();
  const mounted = useMounted();
  const fav = mounted && has(recipe.id);
  return (
    <a
      href={href}
      onClick={e => {
        // Plain left-click → SPA nav; let modified clicks open a new tab.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        navigate(href);
      }}
      className="group block focus-visible:outline-none focus-visible:ring-4 ring-ring/50 rounded-xl"
    >
      <Card className="overflow-hidden h-full flex flex-col p-0 transition-shadow hover:shadow-lg">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={recipe.main_image_thumbs.medium_landscape}
            alt={recipe.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <button
            type="button"
            aria-label={fav ? T.removeFavorite[lang] : T.addFavorite[lang]}
            aria-pressed={fav}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              toggle(recipe.id);
            }}
            className="absolute right-2 top-2 inline-flex size-9 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm transition-colors hover:bg-background"
          >
            <Heart
              className={`size-5 transition-colors ${fav ? "fill-red-500 stroke-red-500" : "text-foreground/70"}`}
            />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 p-4">
          {titleHtml ? (
            <h3
              className="font-semibold leading-snug line-clamp-2 [&_mark]:bg-primary/20 [&_mark]:text-foreground [&_mark]:rounded-sm"
              dangerouslySetInnerHTML={{ __html: titleHtml }}
            />
          ) : (
            <h3 className="font-semibold leading-snug line-clamp-2">{recipe.title}</h3>
          )}

          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {recipe.total_time != null && (
              <span className="inline-flex items-center gap-1">
                <Clock className="size-4" /> {recipe.total_time} min
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Soup className="size-4" /> {recipe.servings_in_char}
            </span>
            <span className="inline-flex items-center gap-1">
              <Star className="size-4" /> {difficultyLabel(recipe.difficulty, lang)}
            </span>
          </div>

          {recipe.categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {recipe.categories.slice(0, 3).map(c => (
                <span
                  key={c.id}
                  className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
                >
                  {c.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </Card>
    </a>
  );
}
